import { type CSSProperties, type RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react'

interface UseLayoutContentHeightProps {
  contentStyle?: CSSProperties
  showHeader?: boolean
}

interface UseLayoutContentHeightResult {
  layoutWrapperRef: RefObject<HTMLDivElement | null>
  resolvedContentStyle: CSSProperties
}

function useLayoutContentHeight({ contentStyle, showHeader }: UseLayoutContentHeightProps): UseLayoutContentHeightResult {
  const layoutWrapperRef = useRef<HTMLDivElement | null>(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useLayoutEffect(() => {
    const wrapper = layoutWrapperRef.current

    if (!wrapper || showHeader === false) {
      setHeaderHeight(0)
      return
    }

    const header = wrapper.querySelector('.ant-layout-header') as HTMLElement | null

    if (!header) {
      setHeaderHeight(0)
      return
    }

    const updateHeaderHeight = () => {
      const nextHeaderHeight = Math.ceil(header.getBoundingClientRect().height)
      setHeaderHeight(currentHeaderHeight =>
        currentHeaderHeight === nextHeaderHeight ? currentHeaderHeight : nextHeaderHeight,
      )
    }

    updateHeaderHeight()

    const resizeObserver = new ResizeObserver(updateHeaderHeight)
    resizeObserver.observe(header)
    window.addEventListener('resize', updateHeaderHeight)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateHeaderHeight)
    }
  }, [showHeader])

  const resolvedContentStyle = useMemo(
    () => ({
      padding: 40,
      minHeight: `calc(100dvh - ${headerHeight}px)`,
      ...contentStyle,
    }),
    [contentStyle, headerHeight],
  )

  return { layoutWrapperRef, resolvedContentStyle }
}

export default useLayoutContentHeight
